import { useState, useEffect } from 'react'
import axios from 'axios'

import { logger } from '@/utils/logger';

function DeviceIcon({ deviceType }) {
  const d =
    deviceType === 'mobile' || deviceType === 'tablet'
      ? 'M17 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM11 18h2'
      : 'M3 4h18v12H3zM8 20h8M12 16v4'
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

export default function AdminSessionsPage() {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(null)
  const [showLegacyNotice, setShowLegacyNotice] = useState(false)

  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/v1/auth/sessions', {
        withCredentials: true
      })
      setSessions(response.data.sessions || [])
      setError('')

      // Check if legacy token was upgraded
      if (response.data.legacy_token_upgraded) {
        setShowLegacyNotice(true)
        // Hide notice after 10 seconds
        setTimeout(() => setShowLegacyNotice(false), 10000)
      }
    } catch (err) {
      setError('Fehler beim Laden der Sessions')
      logger.error('Error fetching sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteSession = async (sessionId) => {
    if (!confirm('Möchten Sie diese Session wirklich beenden?')) {
      return
    }

    try {
      setActionLoading(sessionId)

      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrf_token='))
        ?.split('=')[1]

      await axios.delete(`/api/v1/auth/sessions/${sessionId}`, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })
      // Refresh sessions list
      await fetchSessions()
    } catch (err) {
      setError('Fehler beim Beenden der Session')
      logger.error('Error deleting session:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const handleLogoutAll = async () => {
    if (!confirm('Möchten Sie wirklich alle anderen Sessions beenden? Sie bleiben nur auf diesem Gerät angemeldet.')) {
      return
    }

    try {
      setActionLoading('all')

      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrf_token='))
        ?.split('=')[1]

      await axios.post('/api/v1/auth/sessions/logout-all', {}, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })
      // Refresh sessions list
      await fetchSessions()
    } catch (err) {
      setError('Fehler beim Beenden der Sessions')
      logger.error('Error logging out all sessions:', err)
    } finally {
      setActionLoading(null)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const deviceLabel = (deviceType) =>
    deviceType === 'mobile' ? 'Mobiles Gerät' : deviceType === 'tablet' ? 'Tablet' : 'Desktop'

  if (loading) {
    return (
      <div>
        <h1 style={{ fontSize: 24, margin: '0 0 24px' }}>Aktive Sessions</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div className="wc-spinner" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Aktive Sessions</h1>
        {sessions.length > 1 && (
          <button onClick={handleLogoutAll} disabled={actionLoading === 'all'} className="wc-btn wc-btn-secondary">
            {actionLoading === 'all' ? 'Wird bearbeitet...' : 'Alle anderen beenden'}
          </button>
        )}
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {showLegacyNotice && (
        <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>
          <strong>Session erstellt:</strong> Ihre aktuelle Session wurde automatisch registriert. Beim nächsten Login
          wird diese Session automatisch erstellt.
        </div>
      )}

      <div className="wc-alert wc-alert-info" style={{ marginBottom: 24 }}>
        <strong>Hinweis:</strong> Hier sehen Sie alle Geräte, auf denen Sie aktuell angemeldet sind. Sie können
        einzelne Sessions beenden, wenn Sie sich auf einem Gerät abmelden möchten, ohne Zugriff darauf zu haben.
      </div>

      {sessions.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', padding: '48px 0' }}>
          <p className="wc-muted" style={{ margin: 0 }}>Keine aktiven Sessions gefunden.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          {sessions.map((session) => (
            <div
              key={session.id}
              className="wc-card wc-elev-sm"
              style={{
                gap: 16,
                border: session.is_current ? '1px solid var(--wc-accent)' : '1px solid var(--wc-divider)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flexShrink: 0, color: 'var(--wc-accent)' }}>
                  <DeviceIcon deviceType={session.device_type} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {session.browser} auf {session.os}
                    {session.is_current && <span className="wc-tag wc-tag-accent">Diese Session</span>}
                  </h3>
                  <p className="wc-muted" style={{ fontSize: 13, margin: '4px 0 0' }}>{deviceLabel(session.device_type)}</p>
                </div>
              </div>

              <div className="wc-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                <div><span style={{ color: 'var(--wc-text)' }}>IP-Adresse:</span> {session.ip_address}</div>
                <div><span style={{ color: 'var(--wc-text)' }}>Angemeldet seit:</span> {formatDate(session.created_at)}</div>
                <div><span style={{ color: 'var(--wc-text)' }}>Letzte Aktivität:</span> {formatDate(session.last_activity)}</div>
              </div>

              {!session.is_current ? (
                <button
                  onClick={() => handleDeleteSession(session.id)}
                  disabled={actionLoading === session.id}
                  className="wc-btn wc-btn-secondary wc-btn-block"
                  style={{ color: '#fca5a5' }}
                >
                  {actionLoading === session.id ? 'Wird beendet...' : 'Session beenden'}
                </button>
              ) : (
                <div className="wc-muted" style={{ textAlign: 'center', fontSize: 13, padding: '8px 0' }}>
                  Sie sind aktuell auf diesem Gerät angemeldet
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
